import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FIELD_CLASS, FormLabel } from "./FormField";

// Select custom con campo di ricerca: sostituisce la <select> nativa quando la
// lista di opzioni è lunga (es. gli utenti) e serve filtrarla digitando.
// L'API resta volutamente vicina a quella della select nativa: i valori sono
// stringhe e la stringa vuota "" rappresenta "nessuna selezione".

export interface SearchableSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  // Testo mostrato quando non c'è nessuna selezione.
  placeholder?: string;
  // Se valorizzato aggiunge in cima l'opzione che azzera la selezione
  // (equivalente di <option value="">…</option>).
  emptyOptionLabel?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

// Confronto "tollerante": ignora maiuscole e accenti, così "Perro" trova "Però".
const normalize = (text: string) =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Ogni parola digitata deve comparire nell'etichetta: "ros mar" trova
// "Rossi Mario" anche se l'ordine non coincide con quello mostrato.
const matchesQuery = (label: string, tokens: string[]) => {
  if (!tokens.length) return true;
  const normalizedLabel = normalize(label);
  return tokens.every((token) => normalizedLabel.includes(token));
};

interface MenuPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

// Altezza minima che vogliamo garantire al menu prima di aprirlo verso l'alto.
const MIN_MENU_HEIGHT = 200;
const MAX_MENU_HEIGHT = 288;
const MENU_GAP = 4;

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Seleziona",
  emptyOptionLabel,
  searchPlaceholder = "Cerca...",
  noResultsLabel = "Nessun risultato",
  disabled = false,
  className,
  id,
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const listboxId = `${useId()}-listbox`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // L'opzione "vuota" è a tutti gli effetti un'opzione: viene filtrata come le
  // altre e può essere selezionata con la tastiera.
  const allOptions = useMemo<SearchableSelectOption[]>(
    () => (emptyOptionLabel != null ? [{ value: "", label: emptyOptionLabel }, ...options] : options),
    [emptyOptionLabel, options]
  );

  const visibleOptions = useMemo(() => {
    const tokens = normalize(query).split(/\s+/).filter(Boolean);
    return allOptions.filter((option) => matchesQuery(option.label, tokens));
  }, [allOptions, query]);

  const selectedOption = useMemo(
    () => allOptions.find((option) => option.value === value && option.value !== ""),
    [allOptions, value]
  );

  // Il menu è in un portal (fuori dai contenitori con overflow dei modali), per
  // cui la posizione va calcolata a mano e tenuta aggiornata su scroll/resize.
  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP * 2;
    const spaceAbove = rect.top - MENU_GAP * 2;
    const openUpwards = spaceBelow < MIN_MENU_HEIGHT && spaceAbove > spaceBelow;

    setMenuPosition({
      left: rect.left,
      width: rect.width,
      ...(openUpwards
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
      maxHeight: Math.max(120, Math.min(MAX_MENU_HEIGHT, openUpwards ? spaceAbove : spaceBelow)),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, updateMenuPosition, visibleOptions.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => updateMenuPosition();
    // `true` in capture: intercetta anche lo scroll dei contenitori interni.
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen, updateMenuPosition]);

  // Chiusura al click fuori (trigger e menu esclusi).
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    searchRef.current?.focus();
  }, [isOpen]);

  // Tiene l'opzione evidenziata dentro l'area visibile durante la navigazione.
  useEffect(() => {
    if (!isOpen) return;
    const list = listRef.current;
    const highlighted = list?.children[highlightedIndex] as HTMLElement | undefined;
    highlighted?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, isOpen]);

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    // Si parte dall'opzione già selezionata, come fa la select nativa.
    const selectedIndex = allOptions.findIndex((option) => option.value === value);
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const closeMenu = (refocus = true) => {
    setIsOpen(false);
    setQuery("");
    if (refocus) triggerRef.current?.focus();
  };

  const selectOption = (option: SearchableSelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    closeMenu();
  };

  // Sposta l'evidenziazione saltando le opzioni disabilitate.
  const moveHighlight = (direction: 1 | -1) => {
    if (!visibleOptions.length) return;
    setHighlightedIndex((current) => {
      for (let step = 1; step <= visibleOptions.length; step++) {
        const next = (current + direction * step + visibleOptions.length * step) % visibleOptions.length;
        if (!visibleOptions[next]?.disabled) return next;
      }
      return current;
    });
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = visibleOptions[highlightedIndex];
      if (option) selectOption(option);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
    } else if (event.key === "Tab") {
      closeMenu(false);
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openMenu();
    }
  };

  const buttonLabel = selectedOption?.label ?? placeholder;

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        className={`${FIELD_CLASS} bg-white text-left flex items-center justify-between gap-2 ${className ?? ""}`.trim()}
        onClick={() => (isOpen ? closeMenu() : openMenu())}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={`truncate ${selectedOption ? "" : "text-gray-500"}`}>{buttonLabel}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[60] flex flex-col rounded border border-gray-300 bg-white shadow-lg"
            style={{
              left: menuPosition.left,
              width: menuPosition.width,
              top: menuPosition.top,
              bottom: menuPosition.bottom,
              maxHeight: menuPosition.maxHeight,
            }}
          >
            <div className="p-2 border-b border-gray-200">
              <input
                ref={searchRef}
                type="text"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <ul id={listboxId} ref={listRef} role="listbox" className="overflow-y-auto py-1 text-sm">
              {visibleOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;
                return (
                  <li
                    key={option.value || "__empty__"}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled}
                    className={`px-3 py-2 truncate ${
                      option.disabled
                        ? "text-gray-400 cursor-not-allowed"
                        : `cursor-pointer ${isHighlighted ? "bg-blue-500 text-white" : "hover:bg-gray-100"}`
                    } ${isSelected && !option.disabled && !isHighlighted ? "font-semibold" : ""}`}
                    // mousedown invece di click: evita che il blur chiuda il menu prima.
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectOption(option);
                    }}
                    onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                  >
                    {option.label}
                  </li>
                );
              })}
              {!visibleOptions.length && (
                <li className="px-3 py-2 text-gray-500">{noResultsLabel}</li>
              )}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
};

type SearchableSelectFieldProps = SearchableSelectProps & {
  label: React.ReactNode;
  labelClassName?: string;
};

// Variante con label sopra, allineata a `SelectField` di FormField.
export const SearchableSelectField: React.FC<SearchableSelectFieldProps> = ({
  label,
  labelClassName,
  ...selectProps
}) => (
  <FormLabel label={label} className={labelClassName}>
    <SearchableSelect {...selectProps} />
  </FormLabel>
);
